import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Bell, CheckCheck, CheckCircle2, X } from 'lucide-react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchNotifications, readAllNotifications, readNotification, type CompletionNotification } from '@auw/api-client';
import { useChatStore, useUserStore } from '@auw/stores';
import { modeToPath, modeToPathWithId, WORKSPACE_MODES } from '@auw/workspace-utils';
import { Button } from '../common/Button';
import { Tabs } from '../common/Tabs';
import { notificationKey, useNotificationInvalidation, useNotificationSummary } from './useNotifications';
import { playCompletionSound, primeNotificationAudio } from './sounds';

export function NotificationBell() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const client = useQueryClient();
  const account = useUserStore(state => state.user?.userId ?? '');
  const summary = useNotificationSummary();
  useNotificationInvalidation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('unread');
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState({ left: 8, bottom: 64 });
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const startedAt = useRef(Date.now());
  const latestSeen = useRef<string | null>(null);
  const inbox = useInfiniteQuery({
    queryKey: [...notificationKey(account), 'inbox', tab],
    queryFn: ({ pageParam }) => fetchNotifications(pageParam, tab === 'unread'),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const count = pages.reduce((sum, page) => sum + page.items.length, 0);
      return count < last.total ? count : undefined;
    },
    enabled: open && !!account, refetchInterval: open ? 5000 : false,
  });
  const unread = summary.data?.unread_count ?? 0;
  // A newly inserted notification can shift offset pagination while older
  // pages remain cached. Render each persisted completion only once.
  const entriesById = new Map<string, CompletionNotification>();
  for (const entry of inbox.data?.pages.flatMap(page => page.items) ?? []) {
    if (!entriesById.has(entry.notification_id)) entriesById.set(entry.notification_id, entry);
  }
  const entries = [...entriesById.values()];
  useEffect(() => {
    startedAt.current = Date.now(); latestSeen.current = null; setOpen(false);
  }, [account]);
  useEffect(() => {
    const prime = () => primeNotificationAudio();
    document.addEventListener('pointerdown', prime, { passive: true });
    document.addEventListener('keydown', prime);
    return () => { document.removeEventListener('pointerdown', prime); document.removeEventListener('keydown', prime); };
  }, []);
  useEffect(() => {
    const latest = summary.data?.items[0];
    if (!latest || latest.notification_id === latestSeen.current) return;
    latestSeen.current = latest.notification_id;
    if (Date.parse(latest.created_at) < startedAt.current || latest.read_at || !summary.data?.settings.sound_enabled) return;
    void playCompletionSound(account, latest.notification_id, summary.data.settings.sound)
      .catch(() => { /* An unavailable audio/lock API must not interrupt the inbox. */ });
  }, [summary.data, account]);
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const box = button.current?.getBoundingClientRect();
      if (box) setPosition({ left: Math.max(8, Math.min(box.right - 380, window.innerWidth - 388)),
        bottom: Math.max(8, window.innerHeight - box.top + 10) });
    };
    const outside = (event: PointerEvent) => {
      if (!popup.current?.contains(event.target as Node) && !button.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    place();
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', key);
    window.addEventListener('resize', place);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', key); window.removeEventListener('resize', place); };
  }, [open]);
  async function mark(entry?: CompletionNotification) {
    setBusy(true);
    try {
      if (entry) await readNotification(entry.notification_id); else await readAllNotifications();
      await client.invalidateQueries({ queryKey: notificationKey(account) });
    } catch {
      useChatStore.getState().addToast({ type: 'error', title: t('notifications.saveFailed') });
    } finally { setBusy(false); }
  }
  function openEntry(entry: CompletionNotification) {
    void mark(entry); setOpen(false);
    const id = entry.document_id || entry.item_id;
    let path = id ? modeToPathWithId(entry.workspace_type, id) : modeToPath(entry.workspace_type);
    if (entry.document_id && entry.item_id) path += '?chat=' + encodeURIComponent(entry.item_id);
    navigate(path);
  }
  return <>
    <BellButton ref={button} type="button" data-track="sidebar_notifications"
      aria-label={t('notifications.open', { count: unread })} title={t('sidebar.notifications')}
      aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(value => !value)}>
      <Bell size={15} />
      {unread > 0 && <Count>{unread > 99 ? '99+' : unread}</Count>}
    </BellButton>
    {open && createPortal(<Panel ref={popup} role="dialog" aria-label={t('notifications.title')} style={position}>
      <Header><strong>{t('notifications.title')}</strong>
        <Button variant="surface" size="icon" aria-label={t('button.close', { defaultValue: '닫기' })} onClick={() => setOpen(false)}><X size={15} /></Button>
      </Header>
      <Toolbar
        items={[{ id: 'unread', label: t('notifications.unread') }, { id: 'all', label: t('notifications.all') }]}
        activeId={tab} onChange={setTab}
        rightSlot={<ReadAllButton variant="surface" size="sm" disabled={busy || unread === 0} onClick={() => void mark()} leftIcon={<CheckCheck size={14} />}>{t('notifications.readAll')}</ReadAllButton>}
      />
      <List>
        {inbox.isPending ? <Empty>{t('notifications.loading')}</Empty> : inbox.isError ? <Empty>
          {t('notifications.loadFailed')}<Button variant="surface" size="sm" onClick={() => void inbox.refetch()}>{t('notifications.retry')}</Button>
        </Empty> : entries.length === 0 ? <Empty><CheckCircle2 size={26} />{t(tab === 'unread' ? 'notifications.emptyUnread' : 'notifications.empty')}</Empty> : entries.map(entry =>
          <Entry key={entry.notification_id} type="button" $unread={!entry.read_at} onClick={() => openEntry(entry)}>
            <StatusDot $unread={!entry.read_at} /><EntryText>
              <EntryHeader>
                <Meta>{t(WORKSPACE_MODES[entry.workspace_type].labelKey)} · {t('notifications.completed')}</Meta>
                <Time dateTime={entry.created_at}>{new Date(entry.created_at).toLocaleString(i18n.language, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Time>
              </EntryHeader>
              <EntryTitle>{entry.title}</EntryTitle>
            </EntryText>
          </Entry>)}
        {inbox.hasNextPage && <Button variant="surface" size="sm" disabled={inbox.isFetchingNextPage} onClick={() => void inbox.fetchNextPage()}>{t('notifications.loadMore')}</Button>}
      </List>
    </Panel>, document.body)}
  </>;
}
const BellButton = styled.button`
  position: relative; display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border: 0; border-radius: 8px; cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary}; background: transparent;
  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
`;
const Count = styled.span`
  position: absolute; top: -5px; right: -7px; min-width: 16px; height: 16px; padding: 0 3px;
  display: flex; align-items: center; justify-content: center; border-radius: 20px;
  font-size: 9px; font-weight: 700; background: ${({ theme }) => theme.colors.brand};
  color: ${({ theme }) => theme.colors.brandContrastText}; border: 1px solid ${({ theme }) => theme.colors.bgSecondary};
`;
const Panel = styled.div`
  position: fixed; z-index: 3000; width: min(380px, calc(100vw - 16px));
  max-height: min(560px, calc(100dvh - 96px)); display: flex; flex-direction: column; overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 16px;
  background: ${({ theme }) => theme.colors.bgPrimary}; color: ${({ theme }) => theme.colors.textPrimary};
  box-shadow: 0 12px 48px rgba(0,0,0,.22);
`;
const Header = styled.div`display: flex; align-items: center; justify-content: space-between; padding: 16px 18px 4px;`;
const Toolbar = styled(Tabs)`
  gap: 4px; padding: 0 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  > div:last-child { padding-right: 0; }
  [data-active] { padding: 12px 8px; }
  [data-active="true"] { color: ${({ theme }) => theme.colors.brand}; border-color: ${({ theme }) => theme.colors.brand}; }
  [data-active="true"]::after { background: ${({ theme }) => theme.colors.brand}; }
`;
const ReadAllButton = styled(Button)`
  border: 0; background: transparent; padding: 4px 6px; gap: 4px;
  font-size: 12px;
`;
const List = styled.div`min-height: 120px; overflow: auto; padding: 8px; display: flex; flex-direction: column; gap: 4px;`;
const Entry = styled.button<{ $unread: boolean }>`
  display: flex; gap: 10px; align-items: flex-start; text-align: left; padding: 12px; border: 0; border-radius: 10px; cursor: pointer;
  color: inherit; background: ${({ theme, $unread }) => $unread ? theme.colors.brandSoft : 'transparent'};
  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.brand}; outline-offset: -2px; }
`;
const EntryText = styled.span`display: flex; flex: 1; flex-direction: column; gap: 5px; min-width: 0;`;
const EntryHeader = styled.span`display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0;`;
const StatusDot = styled.span<{ $unread: boolean }>`width: 6px; height: 6px; flex-shrink: 0; margin-top: 5px; border-radius: 50%; background: ${({ theme, $unread }) => $unread ? theme.colors.brand : theme.colors.border};`;
const Meta = styled.span`min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; color: ${({ theme }) => theme.colors.textSecondary};`;
const EntryTitle = styled.span`font-size: 13px; font-weight: 500; line-height: 1.5; overflow-wrap: anywhere;`;
const Time = styled.time`flex-shrink: 0; margin-left: auto; text-align: right; white-space: nowrap; font-size: 11px; color: ${({ theme }) => theme.colors.textMuted};`;
const Empty = styled.div`display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 34px 16px; font-size: 13px; color: ${({ theme }) => theme.colors.textMuted};`;
